import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {NgbActiveModal, NgbDate, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormGroup} from '@angular/forms';
import {User} from 'src/app/classes/User';
import {UserService} from '../../../services/user/user.service';
import {PurchasedSubscription} from '../../../classes/PurchasedSubscription';
import {LessonsScheduleService} from "../../../services/lessons-schedule.service";
import {LessonSchedule} from "../../../classes/LessonSchedule";
import {Child} from "../../../classes/Child";
import {ConfirmDialogComponent} from "../../dialog/confirm-dialog/confirm-dialog.component";
import {BookedLessonsService} from "../../../services/booked-lessons.service";

@Component({
  selector: 'app-lessons-schedule-edit',
  styleUrls: ['./booked-lesson-add-modal.component.scss'],
  templateUrl: './booked-lesson-add-modal.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class BookedLessonAddModal implements OnInit {
  public bookingForm: FormGroup;
  errorMessage: string | null = null;

  public allUsers: User[] = [];
  public filteredUsers: User[] = [];
  public userSearch = '';

  public subscriptions: PurchasedSubscription[] = [];
  public children: Child[] = [];

  public minDate: NgbDate | null = null;
  public maxDate: NgbDate | null = null;

  public allLessons: LessonSchedule[] = [];
  public filteredLessons: LessonSchedule[] = [];

  constructor(
    private activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private scheduleService: LessonsScheduleService,
    private modalService: NgbModal,
    private bookedLessonsService: BookedLessonsService,
  ) {
  }

  ngOnInit(): void {
    this.bookingForm = this.formBuilder.group({
      user: [{value: null, disabled: true}],
      child: [{value: null, disabled: true}],
      subscription: [{value: null, disabled: true}],
      date: [{value: null, disabled: true}],
      lesson: [{value: null, disabled: true}],
    });

    this.bookingForm.get('user')?.valueChanges.subscribe((user: User) => {
      if (user?.subscriptions?.length > 0) {
        const paidActiveSubs = user.subscriptions.filter(
          sub => sub.lessonsCountLeft > 0 && sub.isPaid
        );
        this.subscriptions = paidActiveSubs;

        this.bookingForm.get('subscription')?.enable();

        if (paidActiveSubs.length === 1) {
          this.bookingForm.get('subscription')?.setValue(paidActiveSubs[0]);
        } else {
          this.bookingForm.get('subscription')?.setValue(null);
        }
      } else {
        this.subscriptions = [];
        this.bookingForm.get('subscription')?.setValue(null);
        this.bookingForm.get('subscription')?.disable();
      }

      if (user?.children?.length > 0) {
        this.children = user.children;

        this.bookingForm.get('child')?.enable();

        if (this.children.length === 1) {
          this.bookingForm.get('child')?.setValue(this.children[0]);
        } else {
          this.bookingForm.get('child')?.setValue(null);
        }
      } else {
        this.children = [];
        this.bookingForm.get('child')?.setValue(null);
        this.bookingForm.get('child')?.disable();
      }
    });

    this.bookingForm.get('subscription')?.valueChanges.subscribe((sub) => {
      const dateControl = this.bookingForm.get('date');

      if (sub) {
        this.minDate = this.toNgbDate(sub.startDate);
        this.maxDate = this.toNgbDate(sub.endDate);

        dateControl.enable();

        this.scheduleService.getScheduledLessonsByDateRange(sub.startDate, sub.endDate).subscribe({
          next: (lessons) => {
            this.allLessons = lessons;
          },
          error: (err) => {
            this.errorMessage = "Can't fetch lessons schedules";
            console.error(err);
          }
        });
      } else {
        this.minDate = null;
        this.maxDate = null;

        dateControl.disable();
        dateControl.setValue(null);
      }
    });

    this.bookingForm.get('date')?.valueChanges.subscribe((date: NgbDate | null) => {
      if (date === null) {
        this.filteredLessons = [];
        this.bookingForm.get('lesson')?.setValue(null);
        this.bookingForm.get('lesson')?.disable();
        return;
      }

      const jsSelectedDate = new Date(date.year, date.month - 1, date.day);

      const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const selectedWeekdayName = weekdayNames[jsSelectedDate.getDay()];

      this.filteredLessons = this.allLessons.filter((lesson: LessonSchedule) => {
        const lessonStart = this.toNgbDate(lesson.startDate);
        const lessonEnd = this.toNgbDate(lesson.endDate);

        const inRange = date >= lessonStart && date <= lessonEnd;
        const sameWeekday = lesson.dayOfWeek.toLowerCase() === selectedWeekdayName.toLowerCase();
        return inRange && sameWeekday;
      });

      this.bookingForm.get('lesson')?.setValue(null);

      if (this.filteredLessons.length > 0) {
        this.bookingForm.get('lesson')?.enable();
      } else {
        this.bookingForm.get('lesson')?.disable();
      }
    });

    this.userService.getAllUsers(0, 10000).subscribe({
      next: (resp) => {
        this.allUsers = resp.content;
        this.filteredUsers = [...this.allUsers];

        this.bookingForm.get('user')?.enable();
      },
      error: (err) => {
        this.errorMessage = "Can't fetch users";
        console.error(err);
      }
    });
  }

  onSearchUserChange(value: string): void {
    this.filteredUsers = this.allUsers.filter(user =>
      (`${user.firstName} ${user.lastName}`).toLowerCase().includes(value.toLowerCase())
    );

    const currentUser = this.bookingForm.get('user')?.value;
    if (currentUser && !this.filteredUsers.includes(currentUser)) {
      this.bookingForm.get('user')?.setValue(null);
    }
  }

  closeModal(sendData: any): void {
    if (sendData === "cancel") {
      this.activeModal.close(sendData);
      return;
    }
    const ngbModalRef = this.modalService.open(ConfirmDialogComponent, {
      scrollable: true,
      windowClass: 'confirm-window',
      size: 'md',
      centered: true,
      backdrop: false,
    });

    ngbModalRef.componentInstance.message = 'Are you sure you want to book lesson on behalf of the user?';
    ngbModalRef.componentInstance.confirmButtonMessage = 'Yes';

    ngbModalRef.result.then(
      (result) => {
        if (result.status === 'confirmed') {
          const y = sendData.value.date.year;
          const m = String(sendData.value.date.month).padStart(2, '0');
          const d = String(sendData.value.date.day).padStart(2, '0');

          this.bookedLessonsService.bookLesson({
            childId: sendData.value.child.id,
            lessonScheduleId: sendData.value.lesson.id,
            purchasedSubscriptionId: sendData.value.subscription.id,
            bookingDate: `${y}-${m}-${d}`,
          }).subscribe({
            next: (resp) => {
              this.activeModal.close(resp);
            },
            error: (err) => {
              this.activeModal.dismiss(err);
            }
          });
        }
      }
    );
  }

  toNgbDate(dateStr: string | null): NgbDate | null {
    if (!dateStr) {
      return null;
    }
    const d = new Date(dateStr);
    return new NgbDate(
      d.getUTCFullYear(),
      d.getUTCMonth() + 1,
      d.getUTCDate()
    );
  }
}
